import { useQuery, useReactiveVar } from '@apollo/client';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { InputBase, Typography, useTheme } from '@mui/material';

import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import _ from 'lodash';
import React, { type ChangeEvent, useState } from 'react';
import { AddButton } from 'src/Administration/Common';
import allFieldDefinitionsQuery from 'src/api/admin/allFieldDefinitionsQuery';
import FieldDefDeleteModal from 'src/components/Modals/FieldDefDeleteModal';
import viewStateVar from 'src/store/viewState';
import type { AllFieldDefsData, FieldDefinition, ScopeFilter, SortChoice } from '../types';
import CreateMetadataFieldModal from './AddEditModals/CreateMetadataFieldModal';
import UpdateMetadataFieldModal from './AddEditModals/UpdateMetadataFieldModal';
import MetadataFieldDefinitionsItem from './MetadataFieldDefinitionsItem';
import styles from './MetadataFieldDefinitionsStyles';

const MetadataFieldDefinitionsComponent: React.FC = () => {
  const classes = styles();
  const theme = useTheme();
  const viewState = useReactiveVar(viewStateVar);
  const [isAddFieldModalOpen, setIsAddFieldModalOpen] = useState<boolean>(false);
  const [searchVal, setSearchVal] = useState<string>('');
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>('all');
  const [sortChoice, setSortChoice] = useState<SortChoice[]>([
    { choice: 'name', ascending: true, active: true },
    { choice: 'slug', ascending: true, active: false },
    { choice: 'type', ascending: true, active: false },
  ]);

  const onChangeSearch = (e: ChangeEvent<HTMLInputElement>): void => {
    setSearchVal(e.target.value);
  };

  const { data: allFieldDefsData } = useQuery<AllFieldDefsData>(allFieldDefinitionsQuery, {
    fetchPolicy: 'network-only',
  });
  if (!allFieldDefsData) return null;

  const allFieldsNode: FieldDefinition[] = allFieldDefsData?.allFieldDefs.edges.map((item) => item.node);
  const allFieldsNodeFilteredBySearch = allFieldsNode.filter((a) =>
    a.name.toUpperCase().includes(searchVal.toUpperCase()),
  );

  const allFieldsNodeFilteredByScope = allFieldsNodeFilteredBySearch.filter((field) => {
    if (scopeFilter === 'all') return true;
    if (scopeFilter === 'asset') {
      // asset-only fields: include 'asset' but not 'collection.gallery'
      return field.scope?.includes('asset') && !field.scope?.includes('collection.gallery');
    }
    if (scopeFilter === 'gallery') {
      // gallery-only fields: include 'collection.gallery' but not 'asset'
      return field.scope?.includes('collection.gallery') && !field.scope?.includes('asset');
    }
    return true;
  });

  let sortedFilteredAllFields: FieldDefinition[];
  if (sortChoice[0].ascending === true && sortChoice[0].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.name);
  } else if (sortChoice[0].ascending === false && sortChoice[0].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.name).reverse();
  } else if (sortChoice[1].ascending === true && sortChoice[1].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.slug);
  } else if (sortChoice[1].ascending === false && sortChoice[1].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.slug).reverse();
  } else if (sortChoice[2].ascending === true && sortChoice[2].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.fieldType);
  } else if (sortChoice[2].ascending === false && sortChoice[2].active === true) {
    sortedFilteredAllFields = _.sortBy(allFieldsNodeFilteredByScope, (item) => item.fieldType).reverse();
  } else {
    sortedFilteredAllFields = allFieldsNodeFilteredByScope;
  }

  const nameSortHandler = (): void => {
    setSortChoice([
      { choice: 'name', ascending: !sortChoice[0].ascending, active: true },
      { choice: 'slug', ascending: sortChoice[1].ascending, active: false },
      { choice: 'type', ascending: sortChoice[2].ascending, active: false },
    ]);
  };
  const slugSortHandler = (): void => {
    setSortChoice([
      { choice: 'name', ascending: sortChoice[0].ascending, active: false },
      { choice: 'slug', ascending: !sortChoice[1].ascending, active: true },
      { choice: 'type', ascending: sortChoice[2].ascending, active: false },
    ]);
  };
  const typeSortHandler = (): void => {
    setSortChoice([
      { choice: 'name', ascending: sortChoice[0].ascending, active: false },
      { choice: 'slug', ascending: sortChoice[1].ascending, active: false },
      { choice: 'type', ascending: !sortChoice[2].ascending, active: true },
    ]);
  };

  const TableHeaders: React.FC = () => {
    return (
      <React.Fragment>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Name</Typography>
            <IconButton onClick={nameSortHandler} style={{ margin: '0px 10px 0px 10px', padding: '0px' }} size="large">
              {sortChoice[0].ascending === true ? (
                <ArrowUpwardIcon color={sortChoice[0].active === true ? 'inherit' : 'disabled'} />
              ) : (
                <ArrowDownwardIcon color={sortChoice[0].active === true ? 'inherit' : 'disabled'} />
              )}
            </IconButton>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Slug</Typography>
            <IconButton onClick={slugSortHandler} style={{ margin: '0px 10px 0px 10px', padding: '0px' }} size="large">
              {sortChoice[1].ascending === true ? (
                <ArrowUpwardIcon color={sortChoice[1].active === true ? 'inherit' : 'disabled'} />
              ) : (
                <ArrowDownwardIcon color={sortChoice[1].active === true ? 'inherit' : 'disabled'} />
              )}
            </IconButton>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Type</Typography>
            <IconButton onClick={typeSortHandler} style={{ margin: '0px 10px 0px 10px', padding: '0px' }} size="large">
              {sortChoice[2].ascending === true ? (
                <ArrowUpwardIcon id="cyp-type-arrow" color={sortChoice[2].active === true ? 'inherit' : 'disabled'} />
              ) : (
                <ArrowDownwardIcon id="cyp-type-arrow" color={sortChoice[2].active === true ? 'inherit' : 'disabled'} />
              )}
            </IconButton>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Editable</Typography>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Highlight If Blank</Typography>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography>Applies To</Typography>
          </div>
        </TableCell>
        <TableCell style={{ backgroundColor: theme.palette.background.paper }}>
          <div style={{ display: 'flex' }}>
            <Typography></Typography>
          </div>
        </TableCell>
      </React.Fragment>
    );
  };

  return (
    <div className={classes.wrapper}>
      {/* <AddFieldModal isAddFieldModalOpen={isAddFieldModalOpen} setIsAddFieldModalOpen={setIsAddFieldModalOpen} /> */}
      {isAddFieldModalOpen && (
        <CreateMetadataFieldModal onClose={setIsAddFieldModalOpen} defaultScopeFilter={scopeFilter} />
      )}
      {viewState.editFieldDefModal && <UpdateMetadataFieldModal />}
      <div className={classes.searchAndButton}>
        <div className={classes.addFieldButton}>
          <AddButton onClick={() => setIsAddFieldModalOpen(true)} id="cyp-add-field-btn">
            Add a Field
          </AddButton>
        </div>
        <div className={classes.fieldSearchBox}>
          <InputBase
            className={classes.searchInput}
            id="cyp-mf-quick-search"
            placeholder={'Quick Search'}
            value={searchVal}
            onChange={onChangeSearch}
          />
        </div>
      </div>
      <div className={classes.scopeFilterWrapper}>
        <button
          type="button"
          className={`${classes.scopeFilterTag} ${scopeFilter === 'all' ? classes.scopeFilterTagActive : ''}`}
          onClick={() => setScopeFilter('all')}
        >
          All
        </button>
        <button
          type="button"
          className={`${classes.scopeFilterTag} ${scopeFilter === 'asset' ? classes.scopeFilterTagActive : ''}`}
          onClick={() => setScopeFilter('asset')}
        >
          Asset Fields
        </button>
        <button
          type="button"
          className={`${classes.scopeFilterTag} ${scopeFilter === 'gallery' ? classes.scopeFilterTagActive : ''}`}
          onClick={() => setScopeFilter('gallery')}
        >
          Gallery Fields
        </button>
      </div>
      <TableContainer
        style={{ width: '98%', margin: '20px 20px 30px 20px', maxHeight: '800px' }}
        className={classes.tableContainer}
      >
        {/* <Typography className={classes.warningText}>
            Warning: Do not edit or delete lowercase system fields as that will cause the system to not function
            properly
          </Typography> */}
        <Table className={classes.metadataTable} stickyHeader>
          <TableHead>
            <TableRow>
              <TableHeaders />
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedFilteredAllFields.map((item) => {
              return <MetadataFieldDefinitionsItem key={item.id} item={item} />;
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <FieldDefDeleteModal />
    </div>
  );
};

export default MetadataFieldDefinitionsComponent;
